import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, CreditCard, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#9ca3af',
      },
      iconColor: '#6366f1',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: false,
};

// Checkout Form Component
function CheckoutForm({ 
  amount, 
  requestId, 
  orderId,
  itemName,
  onSuccess, 
  onCancel,
  paymentType = 'service' // 'service' or 'marketplace'
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // Create payment intent on backend
      const endpoint = paymentType === 'marketplace' 
        ? '/stripe/create-payment-intent/marketplace'
        : '/stripe/create-payment-intent/service';
      
      const payload = paymentType === 'marketplace'
        ? { order_id: orderId }
        : { request_id: requestId };

      const { data } = await axios.post(
        `${API}${endpoint}`,
        payload,
        { headers: getAuthHeaders(), withCredentials: true }
      );

      const { clientSecret, paymentIntentId } = data;

      // Confirm card payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (error) {
        setCardError(error.message);
        toast.error(error.message);
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        setPaymentSuccess(true);
        toast.success('Payment successful!');
        
        // Notify backend of successful payment
        await axios.post(
          `${API}/stripe/confirm-payment`,
          { 
            payment_intent_id: paymentIntentId,
            request_id: requestId,
            order_id: orderId,
            payment_type: paymentType
          },
          { headers: getAuthHeaders(), withCredentials: true }
        );

        setTimeout(() => {
          onSuccess && onSuccess(paymentIntent);
        }, 1500);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setCardError(err.response?.data?.detail || 'Payment failed. Please try again.');
      toast.error(err.response?.data?.detail || 'Payment failed');
      setProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h3>
        <p className="text-gray-600">Your payment of ${amount?.toFixed(2)} has been processed.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount Display */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Amount to pay</span>
          <span className="text-2xl font-bold text-gray-900">${amount?.toFixed(2)}</span>
        </div>
        {itemName && (
          <p className="text-sm text-gray-500 mt-1">{itemName}</p>
        )}
      </div>

      {/* Card Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Card Details
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
          <CardElement 
            options={cardElementOptions} 
            onChange={handleCardChange}
          />
        </div>
        {cardError && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            {cardError}
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
        <Lock className="w-4 h-4 text-gray-400" />
        <span>Your payment is secured with 256-bit SSL encryption</span>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          type="submit"
          className="w-full h-12 bg-[#635BFF] hover:bg-[#4B45C6] text-white font-medium"
          disabled={!stripe || processing || !cardComplete}
          data-testid="stripe-submit-btn"
        >
          {processing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <CreditCard className="w-5 h-5" />
              Pay ${amount?.toFixed(2)}
            </span>
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onCancel}
          disabled={processing}
          data-testid="stripe-cancel-btn"
        >
          Cancel
        </Button>
      </div>

      {/* Stripe Badge */}
      <div className="flex items-center justify-center gap-2 pt-2">
        <svg className="h-6" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
          <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10.8 10.8 0 0 1-4.56 1c-4.01 0-6.83-2.5-6.83-7.14 0-4.07 2.5-7.19 6.4-7.19 3.76 0 5.84 2.86 5.84 7.02 0 .5-.04 1.14-.04 1.39zm-5.75-5.69c-1.22 0-2.04.94-2.2 2.41h4.33c-.1-1.47-.91-2.41-2.13-2.41zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V6.25h3.57l.24 1.04c.63-.49 1.64-1.23 3.34-1.23 2.98 0 5.56 2.75 5.56 7.01 0 4.81-2.58 7.23-5.67 7.23zm-.88-10.62c-.86 0-1.44.26-1.88.63l.03 6.2c.44.38 1.02.63 1.85.63 1.39 0 2.34-1.53 2.34-3.71 0-2.14-.95-3.75-2.34-3.75zM28.24 5.57c-1.47 0-2.65-.34-2.65-1.84 0-1.26 1.18-1.96 2.65-1.96 1.46 0 2.64.7 2.64 1.96 0 1.5-1.17 1.84-2.64 1.84zm2.05 14.43h-4.12V6.35h4.12v13.65zM19.95 20.3c-2.06 0-3.74-.53-4.96-1.28l.84-3.16c1.26.75 3.02 1.28 4.18 1.28.91 0 1.3-.23 1.3-.72 0-.57-.89-.78-2.06-1.2-1.85-.63-4.1-1.78-4.1-4.38 0-2.93 2.27-4.78 5.63-4.78 1.68 0 3.08.36 4.24 1.01l-.84 3.08c-1.01-.55-2.37-.95-3.48-.95-.78 0-1.2.23-1.2.68 0 .55.73.76 1.85 1.15 1.94.66 4.35 1.62 4.35 4.46 0 2.95-2.13 4.81-5.75 4.81zM5.8 20.2c-3.36 0-5.8-2.55-5.8-6.9C0 8.6 2.6 6.06 5.96 6.06c1.51 0 2.78.37 3.77.95l-.87 3.3c-.79-.47-1.76-.86-2.82-.86-1.68 0-2.92 1.25-2.92 3.75 0 2.25 1.11 3.66 2.92 3.66 1.01 0 2-.34 2.86-.91l.83 3.24a8.32 8.32 0 0 1-3.93 1.01z" fill="#6772E5"/>
        </svg>
        <span className="text-xs text-gray-400">Powered by Stripe</span>
      </div>
    </form>
  );
}

// Main Stripe Checkout Component with Elements Provider
export function StripeCheckoutModal({
  isOpen,
  onClose,
  amount,
  requestId,
  orderId,
  itemName,
  onSuccess,
  paymentType = 'service'
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-md bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#635BFF]" />
            Secure Payment
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Elements stripe={stripePromise}>
            <CheckoutForm
              amount={amount}
              requestId={requestId}
              orderId={orderId}
              itemName={itemName}
              onSuccess={onSuccess}
              onCancel={onClose}
              paymentType={paymentType}
            />
          </Elements>
        </CardContent>
      </Card>
    </div>
  );
}

// Inline Stripe Checkout (embedded in page, not modal)
export function StripeCheckoutInline({
  amount,
  requestId,
  orderId,
  itemName,
  onSuccess,
  onCancel,
  paymentType = 'service'
}) {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        amount={amount}
        requestId={requestId}
        orderId={orderId}
        itemName={itemName}
        onSuccess={onSuccess}
        onCancel={onCancel}
        paymentType={paymentType}
      />
    </Elements>
  );
}

export default StripeCheckoutModal;
